import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { collectTracking } from "@/lib/utm";
import LeadFields from "@/components/LeadFields";
import { Download, ArrowRight, FileText } from "lucide-react";

export default function Checklist() {
  const [values, setValues] = useState({
    first_name: "", last_name: "", email: "", mobile: "",
    company: "", buyer_type: "", consent: false,
  });
  const [state, setState] = useState({ leadId: null, submitting: false });

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) return toast.error("Please tick the consent box to continue.");
    setState((s) => ({ ...s, submitting: true }));
    try {
      const { data } = await api.post("/leads/checklist", {
        ...values,
        tracking: collectTracking({ formType: "checklist", leadMagnet: "pfas_risk_checklist", enquiryType: "pfas_src_checklist" }),
      });
      setState({ leadId: data.submission_id, submitting: false });
      toast.success("Your checklist is ready.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
      setState((s) => ({ ...s, submitting: false }));
    }
  };

  const downloadUrl = state.leadId
    ? `${API}/leads/checklist/pdf?lead_id=${encodeURIComponent(state.leadId)}`
    : `${API}/leads/checklist/pdf`;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="kicker mb-4">Free PDF · UK</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight">12 Signs PFAS or Brownfield Contamination Could Delay Your Site</h1>
          <p className="mt-6 text-white/65">
            A practical, commercial checklist for developers, landowners, planning professionals,
            environmental and property advisers, legal teams, lenders and insurers.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/70">
            <li className="flex gap-3"><span className="text-emerald-400 mt-1">▸</span>12 early-warning signs with commercial impact tags</li>
            <li className="flex gap-3"><span className="text-emerald-400 mt-1">▸</span>Yes / No / Not Sure tick boxes and scoring guide</li>
            <li className="flex gap-3"><span className="text-emerald-400 mt-1">▸</span>Major trigger list and documents to gather</li>
            <li className="flex gap-3"><span className="text-emerald-400 mt-1">▸</span>Professional disclaimer and Risk Screen offer</li>
          </ul>
          <div className="mt-8 border border-white/10 p-5 bg-[#0C0E13]">
            <div className="kicker">PFAS Brownfield Risk Screen</div>
            <div className="mt-2 font-display text-2xl leading-tight">From £1,950 + VAT · 5–7 working days</div>
            <p className="mt-2 text-sm text-white/60">
              PFAS-specific source review, Red / Amber / Green decision view, evidence gaps and planning / lender / insurer / transaction flags.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="border border-white/10 bg-[#0C0E13] p-6 md:p-10">
            {!state.leadId ? (
              <form onSubmit={submit} className="space-y-8">
                <LeadFields values={values} onChange={setField}
                  consentLabel="I agree to receive the checklist and relevant PFAS / brownfield risk information from PFAS Risk Intelligence."
                  idPrefix="checklist" />
                <button type="submit" disabled={state.submitting}
                  data-testid="checklist-submit-btn"
                  className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90 disabled:opacity-40">
                  {state.submitting ? "Preparing…" : "Send Me the Checklist"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                </button>
              </form>
            ) : (
              <div className="space-y-6" data-testid="checklist-success">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="kicker text-emerald-400">Ready to download</div>
                </div>
                <h2 className="font-display text-3xl leading-tight">Your PFAS Risk Checklist is ready.</h2>
                <p className="text-white/65">
                  Use it to identify early warning signs before acquisition, planning, construction, sale, funding, insurance or enforcement review.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="checklist-download-btn"
                    className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90"
                  >
                    <Download className="w-4 h-4" strokeWidth={2.5} /> Download Checklist
                  </a>
                  <Link to="/risk-screen" data-testid="checklist-request-risk-screen"
                    className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:border-white/60">
                    Request a PFAS Brownfield Risk Screen
                  </Link>
                </div>
                <div className="text-xs text-white/40 border-t border-white/[0.06] pt-6">
                  Reference: <span className="font-mono-alt tracking-[0.12em]">{state.leadId}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
